// Sales Return module — bill search step of the "+Add" create flow.
//
// Search box + result list, styled after the Billing screen's own list (same
// invoice id / customer name / mobile / date / total amount fields as
// BillingTable), scoped down to a single-column picker inside a Sheet.
import * as React from "react";
import { Search } from "lucide-react";
import moment from "moment";
import { Input } from "ui/input";
import AppImages from "../../Globals/AppImages";

export interface SalesReturnBillPickerProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  results: any[];
  loading: boolean;
  onSelect: (bill: any) => void;
}

export function SalesReturnBillPicker({
  searchText,
  onSearchChange,
  results,
  loading,
  onSelect,
}: SalesReturnBillPickerProps) {
  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden p-5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          placeholder="Search by Invoice ID / Customer Name / Product ID"
          className="pl-9"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border">
        {loading && (
          <div className="p-4 text-sm text-muted-foreground">Searching...</div>
        )}
        {!loading && (results || []).length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">
            {searchText ? "No bills found." : "Type to search for a bill."}
          </div>
        )}
        {!loading &&
          (results || []).map((bill: any, index: number) => (
            <button
              key={bill?.billingId ?? index}
              type="button"
              onClick={() => onSelect(bill)}
              className="flex w-full items-center justify-between gap-3 border-b px-4 py-3 text-left text-sm last:border-b-0 hover:bg-muted"
            >
              <div className="space-y-0.5">
                <div className="flex items-center gap-1 font-medium text-foreground">
                  {bill?.invoiceId}
                  {bill?.isOnlineOrder != 1 && <img src={AppImages.onlineOrderIcon} alt="" />}
                </div>
                <div className="text-muted-foreground">
                  {bill?.customerName} {bill?.mobileNumberList ? `• ${bill.mobileNumberList}` : ""}
                </div>
              </div>
              <div className="shrink-0 space-y-0.5 text-right">
                <div className="tabular-nums text-foreground">₹ {bill?.totalamount}</div>
                <div className="text-muted-foreground">
                  {bill?.date ? moment(bill.date).format("DD-MMM-YYYY") : "-"}
                </div>
              </div>
            </button>
          ))}
      </div>
    </div>
  );
}

export default SalesReturnBillPicker;
